import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import InlineError from '../components/InlineError';
import { PageSkeleton } from '../components/SkeletonLoader';
import {
  ArrowLeft, Shield, Target, TrendingUp, TrendingDown, Brain,
  AlertTriangle, Award, BookOpen, Activity, Clock, Zap,
  ChevronRight, Info, ChevronDown, ChevronUp, Share2, X, Loader
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, CartesianGrid, ReferenceLine
} from 'recharts';
import { formatPrice, formatDate } from '../utils/formatters';
import ShareToForumModal from '../components/ShareToForumModal';
import DisciplineShareCard from '../components/DisciplineShareCard';
import { captureAndShare } from '../utils/shareImage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COMPONENT_ICONS = {
  stop_loss:    Shield,
  risk_reward:  Target,
  strategy:     Brain,
  emotion:      AlertTriangle,
  frequency:    Clock,
  confidence:   Zap,
  journal:      BookOpen,
  sl_violation: Shield,
  risk_profile: Activity,
  daily_loss:   TrendingDown,
};

// options_allocation is tracked-only (weight=0) — excluded from scored components
const SCORED_COMPONENTS = ['stop_loss','risk_reward','strategy','emotion','frequency','confidence','journal','sl_violation','risk_profile','daily_loss'];

const COMPONENT_LABELS = {
  stop_loss:    { name: 'Stop Loss Discipline',   weight: '18%' },
  risk_reward:  { name: 'Risk-Reward Planning',   weight: '14%' },
  strategy:     { name: 'Strategy Consistency',   weight: '14%' },
  emotion:      { name: 'Emotional Control',      weight: '9%'  },
  frequency:    { name: 'Trade Frequency',        weight: '9%'  },
  confidence:   { name: 'Confidence Alignment',   weight: '9%'  },
  journal:      { name: 'Journal Discipline',     weight: '5%'  },
  sl_violation: { name: 'SL Violations',          weight: '5%'  },
  risk_profile: { name: 'Risk Profile Adherence', weight: '9%'  },
  daily_loss:   { name: 'Daily Loss Limit',       weight: '8%'  },
};

function DisciplinePage() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [tradeLogs, setTradeLogs] = useState([]);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [pnlImpact, setPnlImpact] = useState(null);
  const [expandedBucket, setExpandedBucket] = useState(null);
  const [insights, setInsights] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [socialOpen, setSocialOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const shareCardRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analysisRes, logsRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/discipline/analysis`),
        axios.get(`${API_URL}/discipline/trade-logs`),
        axios.get(`${API_URL}/discipline/history`),
      ]);
      setAnalysis(analysisRes.data);
      setTradeLogs(logsRes.data);
      setScoreHistory(historyRes.data);
    } catch (err) {
      console.error('Failed to fetch discipline data:', err);
      setFetchError("Couldn't load discipline data.");
    } finally {
      setLoading(false);
    }
    // Fetch separately so failures here never break the rest of the page
    try {
      const pnlRes = await axios.get(`${API_URL}/discipline/pnl-impact`);
      setPnlImpact(pnlRes.data);
    } catch (err) {
      console.error('Failed to fetch pnl-impact:', err);
    }
    try {
      const insRes = await axios.get(`${API_URL}/discipline/insights`);
      setInsights(insRes.data);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    }
    try {
      const bmRes = await axios.get(`${API_URL}/discipline/benchmark`);
      setBenchmark(bmRes.data);
    } catch (err) {
      console.error('Failed to fetch benchmark:', err);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 81) return '#16C784';
    if (score >= 61) return '#6366F1';
    if (score >= 41) return colors.accent;
    if (score >= 21) return '#f97316';
    return '#ef4444';
  };

  const getProfileColor = (label) => {
    const map = {
      'Disciplined Trader':  '#16C784',
      'Developing Trader':   colors.accent,
      'Reactive Trader':     '#F59E0B',
      'Undisciplined Trader':'#EF4444',
      'Mixed Signals':       '#F59E0B',
      'Insufficient Data':   colors.secondaryText,
    };
    return map[label] || colors.accent;
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return `Today ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
    if (diffDays === 1) return `Yesterday ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
    return formatDate(d);
  };

  if (loading) return <PageSkeleton />;

  if (fetchError) {
    return <InlineError message={fetchError} onRetry={() => { setFetchError(null); fetchData(); }} className="h-64" />;
  }

  if (!analysis) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: colors.loss }} />
        <p style={{ color: colors.secondaryText }}>Failed to load discipline data</p>
      </div>
    );
  }

  const { discipline_score, component_scores, profile, daily_loss_details, options_allocation_details } = analysis;
  const chartData = Object.entries(component_scores)
    .filter(([key]) => SCORED_COMPONENTS.includes(key))
    .map(([key, value]) => ({
      name: COMPONENT_LABELS[key]?.name || key,
      score: value,
    }));

  // Score trend chart data — oldest to newest for left→right progression
  const trendData = [...scoreHistory].reverse().map((h, i) => ({
    index: i,
    score: h.score,
    label: formatTime(h.created_at),
  }));


  return (
    <div className="space-y-4 pb-24" data-testid="discipline-page">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate(-1)} className="app-card p-1.5 rounded-lg" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }}>
            <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
          </button>
          <div>
            <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>Discipline Report</h1>
            <p className="text-[13px]" style={{ color: colors.secondaryText }}>Track your trading discipline</p>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowInfo(v => !v)}
            className="p-1.5 rounded-full"
            style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }}
          >
            <Info className="w-4 h-4" style={{ color: showInfo ? colors.accent : colors.secondaryText }} />
          </button>
          {showInfo && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowInfo(false)} />
              <div className="app-card absolute right-0 top-9 z-50 w-72 rounded-xl p-1.5 shadow-xl"
                style={{ backgroundColor: colors.cardBg, borderWidth: '1px', borderColor: colors.accentBorder }}>
                <div className="absolute -top-1.5 right-3 w-3 h-3 rotate-45"
                  style={{ backgroundColor: colors.cardBg, borderTop: `1px solid ${colors.border}`, borderLeft: `1px solid ${colors.border}` }} />
                <p className="text-[13px] font-semibold mb-2" style={{ color: colors.primaryText }}>About Discipline Score</p>
                <p className="text-[13px] leading-relaxed mb-2" style={{ color: colors.primaryText }}>
                  The Discipline Score answers one question: <span className="font-medium" style={{ color: colors.accent }}>"Are you trading with a plan, or are you gambling?"</span>
                </p>
                <p className="text-[13px] leading-relaxed" style={{ color: colors.secondaryText }}>
                  P&L tells you if you made money. Discipline Score tells you if you <span className="italic">deserved</span> to. A trader can get lucky and make money with bad habits — the discipline score catches that.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Learning nudges */}
      <div className="rounded-xl overflow-hidden" style={{ background: learnOpen ? 'rgba(255,255,255,0.03)' : 'linear-gradient(135deg, #f59e0b18 0%, #f59e0b08 100%)', border: learnOpen ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(245,158,11,0.35)', transition: 'background 0.2s, border-color 0.2s' }}>
        <button onClick={() => setLearnOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" style={{ color: learnOpen ? 'rgba(255,255,255,0.4)' : '#f59e0b' }} />
            <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: learnOpen ? 'rgba(255,255,255,0.4)' : '#fcd34d' }}>Know what drives your score</p>
          </div>
          <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200" style={{ color: learnOpen ? 'rgba(255,255,255,0.25)' : '#f59e0b99', transform: learnOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
        </button>
        {learnOpen && (
          <div className="px-4 pb-4 space-y-1.5">
            {[
              { level: 4, color: '#f59e0b', label: 'Myths That Cost Money',  sub: 'The habits and beliefs that destroy traders' },
              { level: 2, color: '#ef4444', label: 'Protect Your Capital',   sub: 'Risk profile, position sizing, and loss limits' },
            ].map(({ level, color, label, sub }) => (
              <button key={level} onClick={() => navigate(`/learning?view=level&level=${level}&card=0`)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left"
                style={{ background: `${color}0d`, border: `1px solid ${color}22` }}>
                <div className="flex items-center gap-2.5">
                  <BookOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
                  <div>
                    <p className="text-[11px] font-semibold leading-snug" style={{ color }}>{label}</p>
                    <p className="text-[9px] leading-snug" style={{ color: 'rgba(255,255,255,0.35)' }}>{sub}</p>
                  </div>
                </div>
                <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: `${color}50` }} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Main Score Card */}
      <div data-tour="discipline-score-card" className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[13px] font-medium" style={{ color: colors.secondaryText }}>Discipline Score</p>
            <p className="text-4xl font-bold" style={{ color: getScoreColor(discipline_score) }}>
              {discipline_score}
              <span className="text-[17px]" style={{ color: colors.secondaryText }}>/100</span>
            </p>
          </div>
          <div className="w-24 h-14">
            <svg viewBox="0 0 100 55" className="w-full h-full">
              <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke={colors.border} strokeWidth="10" strokeLinecap="round" />
              <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke={getScoreColor(discipline_score)} strokeWidth="10"
                strokeLinecap="round" strokeDasharray={`${(discipline_score / 100) * 125.66} 125.66`} />
            </svg>
          </div>
        </div>

        {/* 5-band spectrum */}
        <div className="mt-3 px-1">
          <div className="relative h-2 rounded-full"
            style={{ background: 'linear-gradient(to right, #ef4444 0%, #f97316 20%, #f59e0b 40%, #6366F1 60%, #16C784 100%)' }}>
            <div className="absolute w-3.5 h-3.5 rounded-full bg-white border-2 shadow"
              style={{
                left: `${Math.min(Math.max(discipline_score, 2), 97)}%`,
                top: '50%', transform: 'translate(-50%, -50%)',
                borderColor: getScoreColor(discipline_score),
              }} />
          </div>
          <div className="flex justify-between mt-1.5">
            {['Impulsive', 'Unstructured', 'Developing', 'Consistent', 'Disciplined'].map(l => (
              <span key={l} className="text-[11px]" style={{ color: colors.secondaryText }}>{l}</span>
            ))}
          </div>
        </div>

        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          <div className="flex items-center justify-between">
            <p className="text-[13px]" style={{ color: colors.secondaryText }}>Behavioral Profile</p>
            <div className="flex items-center gap-1.5">
              {profile.confidence > 0 && (
                <span className="text-[12px]" style={{ color: colors.secondaryText }}>{profile.confidence}% conf</span>
              )}
              <span className="text-[13px] px-1.5 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: `${getProfileColor(profile.profile_type)}20`, color: getProfileColor(profile.profile_type) }}>
                {profile.profile_type}
              </span>
            </div>
          </div>
          <div className="flex gap-2 mt-2.5">
            <button
              onClick={() => setSocialOpen(true)}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[12px] font-bold"
              style={{ background: 'rgba(99,102,241,0.1)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' }}>
              <Share2 className="w-3 h-3" /> Post as Image
            </button>
            <button
              onClick={() => setShareOpen(true)}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[12px] font-bold"
              style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }}>
              <Share2 className="w-3 h-3" /> Forum
            </button>
          </div>
        </div>
      </div>

      {/* P3-1: Peer Benchmarking */}
      {benchmark && !benchmark.insufficient_data && (
        <div data-tour="discipline-benchmark" className="app-card rounded-xl p-3" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>How You Compare</h3>
            <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${colors.accent}15`, color: colors.secondaryText }}>
              vs {benchmark.total_users} traders — anonymous
            </span>
          </div>

          {/* Overall percentile bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[12px]" style={{ color: colors.secondaryText }}>Overall Discipline</span>
              <span className="text-[14px] font-bold" style={{ color: benchmark.overall_percentile >= 70 ? '#16C784' : benchmark.overall_percentile >= 40 ? colors.accent : '#ef4444' }}>
                Top {100 - benchmark.overall_percentile}%
              </span>
            </div>
            <div className="h-2 rounded-full" style={{ backgroundColor: colors.inputBg }}>
              <div className="h-2 rounded-full transition-all"
                style={{
                  width: `${benchmark.overall_percentile}%`,
                  background: benchmark.overall_percentile >= 70
                    ? 'linear-gradient(to right, #16C784aa, #16C784)'
                    : benchmark.overall_percentile >= 40
                    ? `linear-gradient(to right, ${colors.accent}aa, ${colors.accent})`
                    : 'linear-gradient(to right, #ef4444aa, #ef4444)',
                }} />
            </div>
            <p className="text-[11px] mt-1" style={{ color: colors.secondaryText }}>
              Better than {benchmark.overall_percentile}% of traders on this platform
            </p>
          </div>

          {/* Best + weakest component */}
          <div className="grid grid-cols-2 gap-2">
            {benchmark.best_component && (
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(22,199,132,0.08)', border: '1px solid rgba(22,199,132,0.2)' }}>
                <p className="text-[10px] font-medium mb-0.5" style={{ color: '#16C784' }}>Your Strength</p>
                <p className="text-[11px] font-semibold" style={{ color: colors.primaryText }}>{benchmark.best_component.label}</p>
                <p className="text-[12px] font-bold" style={{ color: '#16C784' }}>Top {100 - benchmark.best_component.percentile}%</p>
              </div>
            )}
            {benchmark.weakest_component && (
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <p className="text-[10px] font-medium mb-0.5" style={{ color: '#ef4444' }}>Needs Work</p>
                <p className="text-[11px] font-semibold" style={{ color: colors.primaryText }}>{benchmark.weakest_component.label}</p>
                <p className="text-[12px] font-bold" style={{ color: '#ef4444' }}>Bottom {benchmark.weakest_component.percentile}%</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trader Personality Card */}
      <div data-tour="discipline-personality" className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${getScoreColor(profile.profile_score || discipline_score)}20` }}>
              <Brain className="w-7 h-7" style={{ color: getScoreColor(profile.profile_score || discipline_score) }} />
            </div>
            <div>
              <p className="text-[13px]" style={{ color: colors.secondaryText }}>Trader Personality</p>
              <p className="text-[17px] font-bold" style={{ color: colors.primaryText }}>{profile.profile_type}</p>
              {profile.state !== 'insufficient' && profile.confidence > 0 && (
                <p className="text-[12px]" style={{ color: colors.secondaryText }}>{profile.confidence}% confidence</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-[15px] font-bold" style={{ color: getScoreColor(profile.profile_score || discipline_score) }}>
              {profile.profile_score || discipline_score}
            </p>
            <p className="text-[13px]" style={{ color: colors.secondaryText }}>Score</p>
          </div>
        </div>
      </div>

      {/* Early Warning Signals */}
      {profile.warning_flags && profile.warning_flags.length > 0 && (
        <div className="rounded-xl" style={{ padding: '12px 14px', backgroundColor: 'rgba(239,68,68,0.06)', borderWidth: '1px', borderColor: 'rgba(239,68,68,0.25)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#EF4444', margin: '0 0 8px' }}>Early Warning Signals</h3>
          {profile.warning_flags.map((flag, idx) => (
            <div key={idx} className="flex items-start space-x-2">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
              <p className="text-[13px]" style={{ color: colors.secondaryText }}>{flag}</p>
            </div>
          ))}
        </div>
      )}

      {/* Why This Label / Behavioral Signals */}
      {profile.signals && profile.signals.length > 0 && (
        <div className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: '0 0 10px' }}>
            {profile.state === 'labeled' ? 'Why This Label' : 'Observations'}
          </h3>
          <div className="space-y-2">
            {profile.signals.map((sig, idx) => (
              <div key={idx} className="flex items-start space-x-2 p-1.5 rounded" style={{ backgroundColor: colors.inputBg }}>
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: colors.accent }} />
                <p className="text-[13px]" style={{ color: colors.secondaryText }}>{sig}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Behavior Metrics */}
      <div data-tour="discipline-behavior-metrics" className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: '0 0 10px' }}>Behavior Metrics</h3>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Strategy Trades', value: `${profile.metrics.strategy_ratio ?? profile.metrics.planned_rate ?? 0}%`, color: colors.profit },
            { label: 'Impulse Trades',  value: `${profile.metrics.impulse_ratio ?? 0}%`, color: (profile.metrics.impulse_ratio ?? 0) > 30 ? colors.loss : colors.primaryText },
            { label: 'Avg Confidence',  value: `${profile.metrics.confidence_avg ?? 0}/10`, color: colors.primaryText },
            { label: 'Trades/Day',      value: `${profile.metrics.trades_per_day}`, color: profile.metrics.trades_per_day > 8 ? colors.loss : colors.primaryText },
            { label: 'Win Rate',        value: `${profile.metrics.win_rate}%`, color: profile.metrics.win_rate >= 50 ? colors.profit : colors.loss },
            { label: 'Revenge Trades',  value: `${profile.metrics.revenge_trades ?? 0}`, color: (profile.metrics.revenge_trades ?? 0) > 2 ? colors.loss : colors.primaryText },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ padding: '10px 12px', borderRadius: 12, background: colors.inputBg, borderLeft: `3px solid ${color}40` }}>
              <p style={{ fontSize: 10, color: colors.secondaryText, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
              <p style={{ fontSize: 18, fontWeight: 800, color, margin: '2px 0 0', fontVariantNumeric: 'tabular-nums' }}>{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Discipline P&L Impact */}
      {pnlImpact && Array.isArray(pnlImpact.buckets) && pnlImpact.buckets.length > 0 && (
        <div data-tour="discipline-pnl-impact" className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: 0 }}>What's Costing / Making You Money</h3>
            <span className="text-[12px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
              {pnlImpact.total_closed_trades} trades
            </span>
          </div>
          <div className="space-y-2">
            {pnlImpact.buckets.map((bucket) => {
              const isPositive = bucket.pnl >= 0;
              const barColor = isPositive ? '#16C784' : '#ef4444';
              const maxAbs = Math.max(...pnlImpact.buckets.map(b => Math.abs(b.pnl)), 1);
              const barWidth = Math.round((Math.abs(bucket.pnl) / maxAbs) * 100);
              const isExpanded = expandedBucket === bucket.key;

              return (
                <div key={bucket.key} className="rounded-lg overflow-hidden" style={{ borderWidth: '1px', borderColor: `${barColor}30` }}>
                  {/* Bucket header — tap to expand */}
                  <button
                    className="w-full space-y-1 p-2 text-left"
                    style={{ backgroundColor: `${barColor}08` }}
                    onClick={() => setExpandedBucket(isExpanded ? null : bucket.key)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[12px]" style={{ color: colors.secondaryText }}>
                        {bucket.label}
                        <span className="ml-1 opacity-60">({bucket.count})</span>
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[13px] font-bold" style={{ color: barColor }}>
                          {isPositive ? '+' : '-'}₹{Math.abs(bucket.pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </span>
                        {isExpanded
                          ? <ChevronUp className="w-3 h-3" style={{ color: colors.secondaryText }} />
                          : <ChevronDown className="w-3 h-3" style={{ color: colors.secondaryText }} />}
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ backgroundColor: colors.inputBg }}>
                      <div className="h-1.5 rounded-full" style={{ width: `${barWidth}%`, backgroundColor: barColor }} />
                    </div>
                  </button>

                  {/* Individual trades */}
                  {isExpanded && (
                    <div className="divide-y" style={{ borderColor: `${colors.border}50` }}>
                      {(bucket.trades || []).map((trade) => {
                        const tPos = trade.pnl >= 0;
                        const tColor = tPos ? '#16C784' : '#ef4444';
                        // Highlight irony: bad discipline that made money, or good discipline that lost
                        const badBuckets = ['sl_hit', 'no_sl', 'emotional', 'low_confidence'];
                        const goodBuckets = ['sl_respected', 'planned', 'high_confidence', 'target_hit'];
                        const isBadChoice = badBuckets.includes(bucket.key);
                        const isGoodChoice = goodBuckets.includes(bucket.key);
                        const luckyWin = isBadChoice && tPos;
                        const unluckyLoss = isGoodChoice && !tPos;
                        return (
                          <div key={trade.id} className="px-2 py-2" style={{ backgroundColor: colors.inputBg }}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: `${tColor}20` }}>
                                  {tPos
                                    ? <TrendingUp className="w-3 h-3" style={{ color: tColor }} />
                                    : <TrendingDown className="w-3 h-3" style={{ color: tColor }} />}
                                </div>
                                <div>
                                  <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{trade.symbol}</p>
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {trade.exit_reason && (
                                      <span className="text-[11px] px-1 rounded"
                                        style={{
                                          backgroundColor: trade.exit_reason === 'SL' ? `${colors.loss}20` : trade.exit_reason === 'TARGET' ? `${colors.profit}20` : `${colors.accent}20`,
                                          color: trade.exit_reason === 'SL' ? colors.loss : trade.exit_reason === 'TARGET' ? colors.profit : colors.accent,
                                        }}>
                                        {trade.exit_reason}
                                      </span>
                                    )}
                                    <span className="text-[11px]" style={{ color: colors.secondaryText }}>
                                      {trade.entry_price && trade.exit_price
                                        ? `₹${trade.entry_price} → ₹${trade.exit_price}`
                                        : `Conf: ${trade.confidence}/10`}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <p className="text-[13px] font-bold" style={{ color: tColor }}>
                                {tPos ? '+' : '-'}₹{Math.abs(trade.pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </p>
                            </div>
                            {luckyWin && (
                              <p className="text-[11px] mt-1 ml-8" style={{ color: '#f59e0b' }}>
                                ⚠ Wrong approach, got lucky — don't repeat this
                              </p>
                            )}
                            {unluckyLoss && (
                              <p className="text-[11px] mt-1 ml-8" style={{ color: colors.secondaryText }}>
                                ✓ Right approach, bad outcome — keep the discipline
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* -- P2 Intelligence Insights ----------------------------------- */}
      {insights && (
        <>
          {/* 1. Losing Streak Warning */}
          {insights.streak.losing_streak >= 2 && (
            <div className="rounded-xl p-3 flex items-start gap-2"
              style={{
                backgroundColor: insights.streak.losing_streak >= 3 ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
                borderWidth: '1px',
                borderColor: insights.streak.losing_streak >= 3 ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)',
              }}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5"
                style={{ color: insights.streak.losing_streak >= 3 ? '#ef4444' : '#f59e0b' }} />
              <div>
                <p className="text-[13px] font-semibold"
                  style={{ color: insights.streak.losing_streak >= 3 ? '#ef4444' : '#f59e0b' }}>
                  {insights.streak.losing_streak} Losing Sessions in a Row
                </p>
                <p className="text-[13px] mt-0.5" style={{ color: colors.secondaryText }}>
                  {insights.streak.warning}
                </p>
              </div>
            </div>
          )}

          {/* 2. Time-of-Day P&L */}
          {insights.time_of_day.by_hour.length > 0 && (
            <div className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: 0 }}>P&L by Time of Day</h3>
                <Clock className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
              </div>
              {insights.time_of_day.worst_hour && (
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 p-2 rounded-lg text-center" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                    <p className="text-[11px]" style={{ color: colors.secondaryText }}>Worst Hour</p>
                    <p className="text-[15px] font-bold" style={{ color: '#ef4444' }}>{insights.time_of_day.worst_hour.label}</p>
                    <p className="text-[12px]" style={{ color: '#ef4444' }}>
                      avg {insights.time_of_day.worst_hour.avg_pnl < 0 ? '-' : '+'}₹{Math.abs(insights.time_of_day.worst_hour.avg_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div className="flex-1 p-2 rounded-lg text-center" style={{ backgroundColor: 'rgba(22,199,132,0.08)', border: '1px solid rgba(22,199,132,0.2)' }}>
                    <p className="text-[11px]" style={{ color: colors.secondaryText }}>Best Hour</p>
                    <p className="text-[15px] font-bold" style={{ color: '#16C784' }}>{insights.time_of_day.best_hour.label}</p>
                    <p className="text-[12px]" style={{ color: '#16C784' }}>
                      avg {insights.time_of_day.best_hour.avg_pnl < 0 ? '-' : '+'}₹{Math.abs(insights.time_of_day.best_hour.avg_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                {insights.time_of_day.by_hour.map((h) => {
                  const isPos = h.avg_pnl >= 0;
                  const barColor = isPos ? '#16C784' : '#ef4444';
                  const maxAbs = Math.max(...insights.time_of_day.by_hour.map(x => Math.abs(x.avg_pnl)), 1);
                  const barW = Math.round((Math.abs(h.avg_pnl) / maxAbs) * 100);
                  return (
                    <div key={h.hour} className="flex items-center gap-2">
                      <span className="text-[12px] w-10 flex-shrink-0 text-right" style={{ color: colors.secondaryText }}>{h.label}</span>
                      <div className="flex-1 h-5 rounded relative" style={{ backgroundColor: colors.inputBg }}>
                        <div className="h-5 rounded" style={{ width: `${barW}%`, backgroundColor: `${barColor}40` }} />
                        <span className="absolute left-1.5 top-0 bottom-0 flex items-center text-[11px] font-medium" style={{ color: barColor }}>
                          {isPos ? '+' : '-'}₹{Math.abs(h.avg_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })} avg · {h.trade_count}t
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 3. Confidence Calibration */}
          {(insights.confidence_calibration.tiers.low.count > 0 ||
            insights.confidence_calibration.tiers.mid.count > 0 ||
            insights.confidence_calibration.tiers.high.count > 0) && (
            <div className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: 0 }}>Does Your Confidence Predict Outcome?</h3>
                <Zap className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
              </div>
              {insights.confidence_calibration.insight && (
                <div className="mb-2 px-2 py-1.5 rounded-lg" style={{ backgroundColor: `${colors.accent}15`, border: `1px solid ${colors.accentBorder}` }}>
                  <p className="text-[12px]" style={{ color: colors.accent }}>{insights.confidence_calibration.insight}</p>
                </div>
              )}
              <div className="space-y-2">
                {['low', 'mid', 'high'].map((tier) => {
                  const t = insights.confidence_calibration.tiers[tier];
                  if (t.count === 0) return null;
                  const isPos = t.avg_pnl >= 0;
                  const c = isPos ? '#16C784' : '#ef4444';
                  const tierColors = { low: '#6366F1', mid: '#f59e0b', high: '#16C784' };
                  return (
                    <div key={tier} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                      <div>
                        <p className="text-[12px] font-medium" style={{ color: tierColors[tier] }}>{t.label}</p>
                        <p className="text-[11px]" style={{ color: colors.secondaryText }}>{t.count} trades</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[14px] font-bold" style={{ color: c }}>
                          {isPos ? '+' : '-'}₹{Math.abs(t.avg_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })} avg
                        </p>
                        <p className="text-[11px]" style={{ color: colors.secondaryText }}>
                          total {t.total_pnl < 0 ? '-' : '+'}₹{Math.abs(t.total_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 4. Regime vs Discipline */}
          {insights.regime_discipline.length > 0 && (
            <div className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: 0 }}>Discipline by Market Regime</h3>
                <TrendingUp className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
              </div>
              <p className="text-[12px] mb-2" style={{ color: colors.secondaryText }}>
                Your avg discipline score & P&L when market is in each regime
              </p>
              <div className="space-y-2">
                {insights.regime_discipline.map((r) => {
                  const regimeColor = r.regime === 'BULLISH' ? '#16C784' : r.regime === 'BEARISH' ? '#ef4444' : '#f59e0b';
                  const pnlPos = r.total_pnl >= 0;
                  return (
                    <div key={r.regime} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] px-1.5 py-0.5 rounded font-medium"
                          style={{ backgroundColor: `${regimeColor}20`, color: regimeColor }}>
                          {r.regime}
                        </span>
                        <span className="text-[12px]" style={{ color: colors.secondaryText }}>{r.days}d</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px]" style={{ color: colors.secondaryText }}>Score</p>
                          <p className="text-[13px] font-bold" style={{ color: getScoreColor(r.avg_score) }}>{r.avg_score}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[11px]" style={{ color: colors.secondaryText }}>P&L</p>
                          <p className="text-[13px] font-bold" style={{ color: pnlPos ? '#16C784' : '#ef4444' }}>
                            {pnlPos ? '+' : '-'}₹{Math.abs(r.total_pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Trade Statistics */}
      <div className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: '0 0 10px' }}>Trade Statistics</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {[
            { label: 'Total', value: profile.metrics.total_trades, color: colors.primaryText },
            { label: 'Wins',  value: profile.metrics.winning_trades, color: colors.profit },
            { label: 'Losses', value: profile.metrics.losing_trades, color: colors.loss },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ padding: '10px', borderRadius: 12, background: colors.inputBg, textAlign: 'center', borderBottom: `3px solid ${color}40` }}>
              <p style={{ fontSize: 22, fontWeight: 800, color, margin: 0, fontVariantNumeric: 'tabular-nums' }}>{value}</p>
              <p style={{ fontSize: 10, color: colors.secondaryText, margin: '2px 0 0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Why Score Changed — timeline */}
      <div className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: '0 0 12px' }}>Why Score Changed</h2>

        {scoreHistory.length === 0 ? (
          <div className="text-center py-1">
            <Activity className="w-8 h-8 mx-auto mb-2" style={{ color: colors.secondaryText }} />
            <p className="text-[13px]" style={{ color: colors.secondaryText }}>No score changes yet — start trading to build your history</p>
          </div>
        ) : (
          <div className="space-y-2">
            {scoreHistory.map((entry, idx) => {
              const improved = entry.delta > 0;
              const deltaColor = improved ? '#16C784' : '#ef4444';
              const isExpanded = expandedHistory[idx];
              const hasReasons = entry.reasons && entry.reasons.length > 0;
              const hasComponents = entry.component_changes && Object.keys(entry.component_changes).length > 0;

              return (
                <div key={idx} className="rounded-xl overflow-hidden"
                  style={{ borderWidth: '1px', borderColor: improved ? '#16C78430' : '#ef444430' }}>
                  <button
                    className="w-full flex items-center justify-between text-left" style={{ padding: '10px 12px' }}
                    style={{ backgroundColor: improved ? '#16C7840a' : '#ef44440a' }}
                    onClick={() => hasReasons || hasComponents
                      ? setExpandedHistory(p => ({ ...p, [idx]: !p[idx] }))
                      : null}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${deltaColor}20` }}>
                        {improved
                          ? <TrendingUp className="w-3.5 h-3.5" style={{ color: deltaColor }} />
                          : <TrendingDown className="w-3.5 h-3.5" style={{ color: deltaColor }} />}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[14px] font-bold" style={{ color: deltaColor }}>
                            {improved ? '+' : ''}{entry.delta}
                          </span>
                          <span className="text-[13px]" style={{ color: colors.secondaryText }}>?</span>
                          <span className="text-[14px] font-bold" style={{ color: colors.primaryText }}>{entry.score}</span>
                          {entry.trigger_symbol && (
                            <span className="text-[13px] px-1.5 py-0.5 rounded font-medium"
                              style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
                              {entry.trigger_symbol.replace('.NS', '')}
                            </span>
                          )}
                        </div>
                        <p className="text-[13px]" style={{ color: colors.secondaryText }}>{formatTime(entry.created_at)}</p>
                      </div>
                    </div>
                    {(hasReasons || hasComponents) && (
                      isExpanded
                        ? <ChevronUp className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.secondaryText }} />
                        : <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.secondaryText }} />
                    )}
                  </button>

                  {isExpanded && (
                    <div style={{ padding: '10px 12px 12px', borderTop: `1px solid ${colors.border}` }} className="space-y-2">
                      {hasComponents && (
                        <div className="space-y-1">
                          {Object.entries(entry.component_changes).map(([key, change]) => {
                            const cDelta = change.delta;
                            const cColor = cDelta > 0 ? '#16C784' : '#ef4444';
                            const label = COMPONENT_LABELS[key]?.name || key;
                            return (
                              <div key={key} className="flex items-center justify-between py-1"
                                style={{ borderBottom: `1px solid ${colors.border}30` }}>
                                <p className="text-[14px]" style={{ color: colors.secondaryText }}>{label}</p>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>{change.before}</span>
                                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>?</span>
                                  <span className="text-[14px] font-semibold" style={{ color: cColor }}>
                                    {change.after} ({cDelta > 0 ? '+' : ''}{cDelta})
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {hasReasons && (
                        <div className="space-y-1 pt-1">
                          {entry.reasons.map((r, ri) => (
                            <div key={ri} className="flex items-start gap-1.5">
                              <span className="text-[13px] mt-0.5" style={{ color: colors.secondaryText }}>
                                {r.startsWith('Excellent') || r.startsWith('Strong') || r.startsWith('All trades') ? '✓' : '✗'}
                              </span>
                              <p className="text-[14px]" style={{ color: colors.secondaryText }}>{r}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Trades Impact */}
      <div className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: 0 }}>Recent Trades Impact</h2>
          <button onClick={() => navigate('/trade-history')} className="text-[13px] flex items-center" style={{ color: colors.accent }}>
            View All <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="space-y-2">
          {tradeLogs.length === 0 ? (
            <div className="text-center py-1">
              <Activity className="w-8 h-8 mx-auto mb-2" style={{ color: colors.secondaryText }} />
              <p className="text-[13px]" style={{ color: colors.secondaryText }}>No trades yet — place your first trade to see impact here</p>
            </div>
          ) : (
            tradeLogs.slice(0, 4).map((trade, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 12, backgroundColor: colors.inputBg }}>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: trade.profit_loss >= 0 ? `${colors.profit}20` : `${colors.loss}20` }}>
                    {trade.profit_loss >= 0
                      ? <TrendingUp className="w-4 h-4" style={{ color: colors.profit }} />
                      : <TrendingDown className="w-4 h-4" style={{ color: colors.loss }} />}
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>
                      {trade.symbol.replace('.NS', '')}
                    </p>
                    <div className="flex items-center space-x-1">
                      <span className="text-[12px] px-1 py-0.5 rounded"
                        style={{
                          backgroundColor: trade.why_this_trade === 'Impulse' || trade.why_this_trade === 'Tip'
                            ? `${colors.loss}20` : `${colors.accent}20`,
                          color: trade.why_this_trade === 'Impulse' || trade.why_this_trade === 'Tip'
                            ? colors.loss : colors.accent
                        }}>
                        {trade.why_this_trade}
                      </span>
                      <span className="text-[12px]" style={{ color: colors.secondaryText }}>Conf: {trade.confidence_score}/10</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[13px] font-bold" style={{ color: trade.profit_loss >= 0 ? colors.profit : colors.loss }}>
                    {trade.profit_loss >= 0 ? '+' : ''}{formatPrice(trade.profit_loss)}
                  </p>
                  <p className="text-[12px]" style={{ color: colors.secondaryText }}>{trade.journal_notes ? '📓' : '—'}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Score Breakdown Chart */}
      <div className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: '0 0 12px' }}>Score Breakdown</h2>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 10 }}>
              <XAxis type="number" domain={[0, 100]} hide />
              <YAxis type="category" dataKey="name" width={110} tick={{ fill: colors.secondaryText, fontSize: 9 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}`, borderRadius: '8px' }}
                formatter={(v) => [`${v}/100`, 'Score']} />
              <Bar dataKey="score" radius={[0, 4, 4, 0]} barSize={14}>
                {chartData.map((entry, i) => <Cell key={i} fill={getScoreColor(entry.score)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Component Details */}
      <div className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: '0 0 12px' }}>Component Details</h2>
        <div className="space-y-2">
          {Object.entries(component_scores).filter(([key]) => SCORED_COMPONENTS.includes(key)).map(([key, score]) => {
            const Icon = COMPONENT_ICONS[key] || Shield;
            const label = COMPONENT_LABELS[key] || { name: key, weight: '' };
            const scoreColor = getScoreColor(score);
            const barW = Math.round(score);
            return (
              <div key={key} style={{ padding: '10px 12px', borderRadius: 12, backgroundColor: colors.inputBg }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 9, background: `${scoreColor}18`, border: `1px solid ${scoreColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon style={{ width: 13, height: 13, color: scoreColor }} />
                    </div>
                    <div>
                      <p style={{ fontSize: 12, fontWeight: 600, color: colors.primaryText, margin: 0 }}>{label.name}</p>
                      <p style={{ fontSize: 10, color: colors.secondaryText, margin: '1px 0 0' }}>Weight: {label.weight}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 16, fontWeight: 800, color: scoreColor, margin: 0, fontVariantNumeric: 'tabular-nums' }}>{score}</p>
                </div>
                <div style={{ height: 4, borderRadius: 4, backgroundColor: `${scoreColor}20` }}>
                  <div style={{ height: 4, borderRadius: 4, width: `${barW}%`, backgroundColor: scoreColor }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* -- Risk Violations -- */}
      {((daily_loss_details?.violations?.length > 0) || (options_allocation_details?.violations > 0)) && (
        <div className="app-card rounded-xl space-y-3"
          style={{ padding: '14px', background: `linear-gradient(135deg, #ef444418 0%, ${colors.cardBg} 100%)`, border: '1px solid #ef444430' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle style={{ width: 16, height: 16, flexShrink: 0, color: '#ef4444' }} />
            <h2 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: 0 }}>Risk Violations</h2>
          </div>

          {/* Daily loss breaches */}
          {daily_loss_details?.violations?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold tracking-widest mb-2" style={{ color: colors.secondaryText }}>DAILY LOSS LIMIT BREACHES</p>
              <div className="space-y-1.5">
                {daily_loss_details.violations.map((v, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg px-2.5 py-2"
                    style={{ background: v.severe ? '#ef444415' : '#f9731612', border: `1px solid ${v.severe ? '#ef444435' : '#f9731630'}` }}>
                    <div>
                      <p className="text-[12px] font-semibold" style={{ color: colors.primaryText }}>{v.date}</p>
                      <p className="text-[10px]" style={{ color: colors.secondaryText }}>
                        Limit ₹{v.limit.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        {v.severe && <span className="ml-1.5 font-bold" style={{ color: '#ef4444' }}>SEVERE</span>}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[13px] font-bold" style={{ color: v.severe ? '#ef4444' : '#f97316' }}>
                        -₹{v.loss.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </p>
                      <p className="text-[10px]" style={{ color: colors.secondaryText }}>
                        {((v.loss / v.limit) * 100).toFixed(0)}% of limit
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Options allocation breaches */}
          {options_allocation_details?.violations_detail?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold tracking-widest mb-2" style={{ color: colors.secondaryText }}>
                OPTIONS OVER-ALLOCATION <span className="font-normal normal-case tracking-normal ml-1" style={{ color: colors.secondaryText }}>(tracked — not scored)</span>
              </p>
              <div className="space-y-1.5">
                {options_allocation_details.violations_detail.map((v, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg px-2.5 py-2"
                    style={{ background: '#f59e0b10', border: '1px solid #f59e0b30' }}>
                    <div>
                      <p className="text-[12px] font-semibold" style={{ color: colors.primaryText }}>{v.symbol}</p>
                      <p className="text-[10px]" style={{ color: colors.secondaryText }}>{v.date} — Limit ₹{v.limit_amt.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[13px] font-bold" style={{ color: '#f59e0b' }}>
                        ₹{v.deployed_amt.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </p>
                      <p className="text-[10px]" style={{ color: colors.secondaryText }}>{v.deployed_pct.toFixed(1)}% of capital</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Score Trend Chart */}
      {trendData.length > 0 && (
        <div className="app-card rounded-xl" style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: '0 0 12px' }}>Score Trend</h2>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ left: -10, right: 10, top: 5, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={`${colors.border}`} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: colors.secondaryText, fontSize: 8 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: colors.secondaryText, fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}`, borderRadius: '8px', fontSize: '12px' }}
                  formatter={(v) => [`${v}/100`, 'Score']}
                />
                <ReferenceLine y={discipline_score} stroke={colors.accent} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="score" stroke={colors.accent} strokeWidth={2}
                  dot={{ fill: colors.accent, r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Improvement Tips */}
      <div className="rounded-xl" style={{ padding: '14px', backgroundColor: `${colors.accent}10`, borderWidth: '1px', borderColor: colors.accent }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <AlertTriangle style={{ width: 16, height: 16, flexShrink: 0, marginTop: 2, color: colors.accent }} />
          <div>
            <p style={{ fontSize: 14, fontWeight: 700, color: colors.primaryText, margin: '0 0 8px' }}>Improvement Tips</p>
            <ul className="space-y-1">
              {discipline_score < 75 && (
                <li className="text-[14px]" style={{ color: colors.secondaryText }}>· Focus on respecting your stop loss levels</li>
              )}
              {profile.metrics.impulse_ratio > 20 && (
                <li className="text-[14px]" style={{ color: colors.secondaryText }}>· Reduce impulse trades — wait for strategy confirmation</li>
              )}
              {profile.metrics.confidence_avg < 6 && (
                <li className="text-[14px]" style={{ color: colors.secondaryText }}>· Build conviction — trade only high-confidence setups</li>
              )}
              {component_scores.journal < 60 && (
                <li className="text-[14px]" style={{ color: colors.secondaryText }}>· Document every trade for better learning</li>
              )}
              {component_scores.risk_profile < 60 && (
                <li className="text-[14px]" style={{ color: colors.secondaryText }}>· Keep trade sizes within your risk profile limit</li>
              )}
              {component_scores.daily_loss < 60 && (
                <li className="text-[14px]" style={{ color: colors.secondaryText }}>· Stop trading for the day once you hit your daily loss limit</li>
              )}
            </ul>
          </div>
        </div>
      <ShareToForumModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        attachments={[{ attach_type: 'discipline', snapshot: { score: discipline_score, component_scores, profile_type: profile.profile_type } }]}
        defaultTitle={`Discipline Score: ${discipline_score}/100 — ${profile.profile_type}`}
        defaultLifecycle="trade_idea"
      />

      {/* Hidden card — full 1080×1440 for capture */}
      <div style={{ position: 'fixed', left: -9999, top: 0, opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
        <DisciplineShareCard discipline_score={discipline_score} component_scores={component_scores} profile_type={profile.profile_type} cardRef={shareCardRef} />
      </div>

      {socialOpen && (() => {
        const PREVIEW_W = 260;
        const SCALE     = PREVIEW_W / 1080;
        const CARD_H    = 1350;
        const PREVIEW_H = Math.round(CARD_H * SCALE);
        return (
          <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setSocialOpen(false)}>
            <div
              className="w-full max-w-sm rounded-t-2xl"
              style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Fixed header */}
              <div style={{ padding: '18px 20px 10px', flexShrink: 0 }}>
                <div className="flex items-center justify-between">
                  <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>Share Discipline Card</p>
                  <button onClick={() => setSocialOpen(false)}><X className="w-4 h-4" style={{ color: colors.secondaryText }} /></button>
                </div>
              </div>
              {/* Scrollable preview */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
                <div style={{ width: PREVIEW_W, height: PREVIEW_H, margin: '0 auto', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ width: 1080, height: CARD_H, transform: `scale(${SCALE})`, transformOrigin: 'top left' }}>
                    <DisciplineShareCard discipline_score={discipline_score} component_scores={component_scores} profile_type={profile.profile_type} />
                  </div>
                </div>
              </div>
              {/* Fixed footer */}
              <div style={{ padding: '12px 20px 20px', flexShrink: 0 }}>
                <p className="text-[12px] text-center mb-3" style={{ color: colors.secondaryText }}>Opens share sheet — pick Instagram, WhatsApp, or save to camera roll.</p>
                <button
                  onClick={async () => { if (!shareCardRef.current) return; setCapturing(true); try { await captureAndShare(shareCardRef.current, 'discipline-score.png'); } finally { setCapturing(false); setSocialOpen(false); } }}
                  disabled={capturing}
                  className="w-full py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
                  style={{ background: colors.accent, color: '#fff', opacity: capturing ? 0.6 : 1 }}>
                  {capturing ? <><Loader className="w-4 h-4 animate-spin" /> Preparing…</> : <><Share2 className="w-4 h-4" /> Share as Image</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      </div>
    </div>
  );
}

export default DisciplinePage;
